import React, { lazy } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon } from 'lucide-react';
import SaveToTracker from '../UI/SaveToTracker';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { handleImgError } from '../../utils/imageUtils';
import { MerchProduct } from '../../data/homePageData';
interface FeaturedMerchCardProps {
  product: MerchProduct;
}
export const FeaturedMerchCard: React.FC<FeaturedMerchCardProps> = ({
  product
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  return <div className="flex-shrink-0 w-[220px] sm:w-[240px] md:w-[260px]" data-id="element-1799" data-mp="l61ai1wqhglm">
      <div className={`group rounded-xl overflow-hidden border transition-all duration-200 hover:shadow-md hover:-translate-y-1 h-full flex flex-col ${dk.card}`} data-id="element-1800" data-mp="l483gli7kud3">
        {/* Image Area */}
        <div className="aspect-square relative bg-gray-200" data-id="element-1801" data-mp="1dogdnb0r304n">
          <img src={product.image} alt={product.title} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" loading="lazy" onError={handleImgError} data-id="element-1802" data-mp="17vanhd3iwimu" />

          {/* Overlay Gradient */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-60" data-id="element-1803" data-mp="1csu5mo3cdfk2"></div>

          {/* Price Badge (Top Right) */}
          <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2 py-1 rounded-full border border-white/10" data-id="element-1804" data-mp="1lmfk06blgusy">
            ${product.price.toFixed(2)}
          </div>

          {/* Official Badge (Top Left) */}
          {product.isOfficialMerch && <div className={`absolute top-3 left-3 ${isAfterTide ? 'bg-[#e2b04a] text-[#1a1a2e]' : 'bg-indigo-500 text-white'} text-[10px] font-bold px-2 py-1 rounded`} data-id="element-1805" data-mp="64ngibwte100">
              OFFICIAL
            </div>}

          {/* Vendor Badge (Bottom Left) */}
          <div className="absolute bottom-3 left-3" data-id="element-1806" data-mp="uvjsfzgzsj71">
            <div className="text-white text-xs font-medium drop-shadow-md" data-id="element-1807" data-mp="uw36h53iunfs">
              by {product.vendor}
            </div>
          </div>
        </div>

        {/* Content — fixed height structure */}
        <div className="p-4 flex-1 flex flex-col min-h-[140px]" data-id="element-1808" data-mp="14rwgohtrq4ue">
          {/* Subcategory Label + Save */}
          <div className="flex items-center justify-between mb-1" data-id="element-1809" data-mp="19r9etyq3ayca">
            <div className={`text-[10px] uppercase tracking-wider font-bold ${rt.textPrimary}`} data-id="element-1810" data-mp="1dt4eyzwab5oh">
              {product.genre || 'Merchandise'}
            </div>
            <SaveToTracker productId={product.id} data-id="element-1811" data-mp="1nj5rcuf9yznk" />
          </div>

          {/* Name */}
          <h3 className={`block text-sm sm:text-base font-bold mb-2 line-clamp-2 h-10 ${dk.textPrimary}`} data-id="element-1812" data-mp="1skmruotukk3m">
            {product.title}
          </h3>

          {/* Tags */}
          <div className="h-6 mb-3" data-id="element-1813" data-mp="3i1qoi9umq15h">
            {product.relatedBook && <Link to={`/book/${product.relatedBook.toLowerCase().replace(/\s+/g, '-')}`} className={`text-[10px] px-2 py-0.5 rounded-full border hover:underline ${dk.badgeNeutral} ${dk.borderSubtle}`} data-id="element-1814" data-mp="3d1w0ce28npo4">
                From: {product.relatedBook}
              </Link>}
          </div>

          {/* Bottom Row */}
          <div className="flex items-center justify-between mt-auto" data-id="element-1815" data-mp="4nna3cn6vaz4">
            <div className="flex items-center text-xs font-medium text-amber-400" data-id="element-1816" data-mp="3vsm0hv0fcyda">
              <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-1817" data-mp="3r02oh2hdybal" />
              4.8
              <span className={`ml-1 ${dk.textFaint}`} data-id="element-1818" data-mp="3m32eighf0oe7">(124)</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
};